import { useRef } from "react";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    isOverflowingX,
    useTruncationTooltip,
} from "@design-system/Tooltip/useTruncationTooltip";
import { TriangleSmallIcon } from "@icons";
import styles from "./ChartOfAccountsTemplateEditor.module.scss";

interface Props {
    issueCount: number;
    // 0-based index of the currently-navigated issue (already clamped).
    currentIssueIndex: number;
    currentIssueMessage: string | undefined;
    onNavigateIssue: (index: number) => void;
    // Noun before "X of Y"; the mapping drawer reads "Unmapped 1 of 3".
    label?: string;
}

export const EditorIssueBar = ({
    issueCount,
    currentIssueIndex,
    currentIssueMessage,
    onNavigateIssue,
    label = "Issue",
}: Props) => {
    const lastIndex = issueCount - 1;
    const isSingleIssue = issueCount === 1;
    const nameRef = useRef<HTMLSpanElement>(null);
    const truncationTooltipProps = useTruncationTooltip({
        ref: nameRef,
        isTruncated: isOverflowingX,
    });
    const navigateTo = (index: number) =>
        onNavigateIssue(Math.min(Math.max(index, 0), lastIndex));

    return (
        <div className={styles.issueBar}>
            <div className={styles.issueBarNav}>
                <Button
                    variant="primary"
                    size="md"
                    iconOnly
                    aria-label="Previous issue"
                    className={styles.issueBarNavLeft}
                    onClick={() => navigateTo(currentIssueIndex - 1)}
                    disabled={!isSingleIssue && currentIssueIndex === 0}
                >
                    <TriangleSmallIcon className="icon-rotate-90" />
                </Button>
                <Button
                    variant="primary"
                    size="md"
                    iconOnly
                    aria-label="Next issue"
                    className={styles.issueBarNavRight}
                    onClick={() => navigateTo(currentIssueIndex + 1)}
                    disabled={!isSingleIssue && currentIssueIndex >= lastIndex}
                >
                    <TriangleSmallIcon className="icon-rotate-270" />
                </Button>
            </div>
            <Tooltip content={currentIssueMessage} {...truncationTooltipProps}>
                <button
                    type="button"
                    className={styles.issueBarMessage}
                    // Clicking the message re-focuses the current issue's cell (same as
                    // re-navigating to it), so the user can always jump back to it.
                    onClick={() => onNavigateIssue(currentIssueIndex)}
                >
                    <span>
                        {label} {currentIssueIndex + 1} of {issueCount}
                    </span>
                    <span className={styles.issueBarDot}>&middot;</span>
                    <span ref={nameRef}>{currentIssueMessage}</span>
                </button>
            </Tooltip>
        </div>
    );
};
